import React, { useEffect, useState } from 'react';
import Placeholder from '../Placholder';
import { CommonModal, DateInput, LoadingBar, MultiSelect, PrimaryButton, Select } from '../../Common';
import Routes from '../../../routes';
import toast from 'react-hot-toast';
import { Box, Grid } from '@mui/material';
import '../../Common/Filters/Filters.css';
import UserInfo from '../../../UserInfo';
import RangeInput from '../../Common/RangeInput';
import { AllOption, CFC, fetchReportExport, getGWP, getLocationFiltersDropdown, getUpdatedLocation, getValueOrDefault, HCFC, renderWeight, validateProperty } from '../utils';
import { createYearValues } from '../../../utils';

const ChronicallyLeaking608 = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [loadingExport, setLoadingExport] = useState(false);

    const [reportModal, setReportModal] = useState(false);
    const [maxGWP, setMaxGWP] = useState();
    const [maxChargeCapacity, setMaxChargeCapacity] = useState();

    const currentYear = new Date().getFullYear();
    const years = createYearValues(currentYear, currentYear - 2020);
    const [formData, setFormData] = useState({
        year: currentYear,
        regions: [],
        locations: [],
        states: [],
        refrigerantType: [],
        refrigerantGwpMin: 0,
        refrigerantGwpMax: "",
        chargeCapacityMin: 50,
        chargeCapacityMax: "",
    });

    const setDefaultFormData = (data) => {

        const maxGwp = getGWP([data.refrigerantType[HCFC],data.refrigerantType[CFC]], data.refrigerantGWP);
        const maxChargeCapacity = renderWeight(data.maxCapacity);
        setFormData(prev => ({
            ...prev,
            year: currentYear,
            states: [data.states[AllOption]],
            locations: [data.locations[AllOption]],
            regions: [data.regions[AllOption]],
            refrigerantType: [data.refrigerantType[HCFC],data.refrigerantType[CFC]],
            chargeCapacityMin: renderWeight(50),
            chargeCapacityMax: maxChargeCapacity,
            refrigerantGwpMin: 0,
            refrigerantGwpMax: maxGwp,
        }));

        setData({
            ...data, 
            filterLocations: data.locations,
            filterStates: data.states,
            filterRegions: data.regions,
        });
        setMaxGWP(maxGwp);
        setMaxChargeCapacity(maxChargeCapacity);

    };

    const fetchDropdown = async () => {
        try {
            const response = await fetch(Routes.PowerBI.EpaDropdown());
            const data = await response.json();
            setDefaultFormData(data);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdown();
    }, []);

    const validateFields = (filter) => {
        const errorObject = {};
        const isEmpty = (value) =>
            value === null || value === undefined || value === "" || value.length === 0;

        const validLocations = new Set(data.filterLocations.map(item => item.value));
        const validStates = new Set(data.filterStates.map(item => item.value));
    
        filter.forEach(({ Property, Value }) => {
            if (isEmpty(Value)) {
                switch (Property) {
                    case "year":
                        errorObject.year = "Year";
                        break;
                    case "regions":
                        errorObject.regions = "Region/Division";
                        break;
                    case "locations":
                        errorObject.locations = "Location Name";
                        break;
                    case "refrigerantType":
                        errorObject.refrigerantType = "Refrigerant Type";
                        break;
                    case "refrigerantTypeGWP":
                        errorObject.refrigerantTypeGWP = "Refrigerant Type GWP";
                        break;
                    case "assetCapacity":
                        errorObject.assetCapacity = "Asset Charge Capacity";
                        break;
                    case "states":
                        errorObject.states = "Location State";
                        break;
                    default:
                        break;
                }
            } else {
                switch (Property) {
                    case "locations":
                        if (!validLocations.has(parseInt(Value))) {
                            errorObject.locations = "Location Name";
                        }
                        break;
                    case "states":
                        if (!Value.split(',').every(state => validStates.has(state))) {
                            errorObject.states = "Location State";
                        }
                        break;                  
                    case "refrigerantTypeGWP":
                        if (!validateProperty(filter, "refrigerantTypeGWP", maxGWP)) {
                            errorObject.refrigerantTypeGWP = "Refrigerant Type GWP";
                        }
                        break;
                    case "assetCapacity":
                        if (!validateProperty(filter, "assetCapacity", maxChargeCapacity)) {
                            errorObject.assetCapacity = "Asset Charge Capacity";
                        }
                        break;
                    default:
                        break;
                }
            }
        });

        const hasError = Object.keys(errorObject).length > 0;
        if (hasError) {
            const errorMessages = Object.keys(errorObject)
                .map((key) => errorObject[key])
                .join(", ");
            toast.error(
                `All required filters must be applied to generate the report. Please select the filter(s) ${errorMessages} and try again.`,
                { duration: 6000 }
            );
        }
        return hasError;
    };

    const onHandleLocationRegionChange = (value, type) => {
        const { regionFilter, stateFilter, locationFilter, newChargeCapacityMax } = getLocationFiltersDropdown(value, type, data, formData, maxChargeCapacity);
        setData(prev => ({
            ...prev,
            filterRegions: regionFilter,
            filterStates: stateFilter,
            filterLocations: locationFilter,
        }));

        const { locations } = getUpdatedLocation(value, type,formData, data.locations);
        const newChargeCapacityMin =  newChargeCapacityMax < 50 ? 0 : renderWeight(50);
        setFormData(prev => ({
            ...prev,
            locations: locations,
            chargeCapacityMin: newChargeCapacityMin,
            chargeCapacityMax: newChargeCapacityMax,
        }));
        setMaxChargeCapacity(newChargeCapacityMax);
    };
    

    const exportReport = async () => {
        const filter = [
            {
                Property: "year",
                Value: formData.year
            },
            {
                Property: "regions",
                Value: formData.regions.map(re => re.value).toString()
            },
            {
                Property: "locations",
                Value: formData.locations.map(location => location.value).toString()
            },
            {
                Property: "refrigerantType",
                Value: formData.refrigerantType.map(re => re.name).toString()
            },
            {
                Property: "refrigerantTypeGWP",
                Value: `${formData.refrigerantGwpMin}-${formData.refrigerantGwpMax}`
            },
            {
                Property: "assetCapacity",
                Value: `${formData.chargeCapacityMin}-${formData.chargeCapacityMax}`
            },
            {
                Property: "states",
                Value: formData.states.map(sta => sta.value).toString()
            },
        ];
        if (validateFields(filter)) {
            return;
        }
        setReportModal(false);
        setLoadingExport(true);
        await fetchReportExport({
            url: Routes.PowerBI.ChronicallyLeaking608Report(),
            body: {
                Id: 31,
                Filters: filter,
            }
        });
        setDefaultFormData(data);
        setLoadingExport(false);
    };

    if (loading) {
        return <LoadingBar />;
    }

    return (
        <>
            <Placeholder 
                message={'Customize and export your report by clicking the button below.'}
                subMessage={''}
            >
                <Grid item xs={12} md={3} sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        marginBottom: '2rem' 
                    }}>
                    <PrimaryButton
                        buttonText="GENERATE REPORT (.zip)"
                        onClick={() => { 
                            setReportModal(true);
                        }}
                    />
                </Grid>
            </Placeholder>

            <CommonModal
                open={reportModal}
                close={() => setReportModal(false)}
                header="Generate Report"
            >
                <Grid container spacing={3} align='center'>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <Select
                            variant={"standard"}
                            options={years}
                            label="Select Year"
                            onChange={e => {
                                setFormData(prev => ({
                                    ...prev,
                                    year: e.target.value
                                }));
                            }
                            }
                            value={formData.year}
                        />
                    </Grid>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <MultiSelect 
                            label='Region/Division *'
                            isFilterObject
                            variant={"standard"}
                            options={data.filterRegions}
                            value={formData.regions}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.regions, "regions", formData);
                                setFormData(prev => ({
                                    ...prev,
                                    regions: newValue,
                                }));
                                onHandleLocationRegionChange(newValue, 'regions');
                            }}
                        >
                        </MultiSelect>
                    </Grid>

                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <MultiSelect 
                            label='Location Name *'
                            isFilterObject
                            variant={"standard"}
                            options={data.filterLocations}
                            value={formData.locations}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.locations, "locations", formData);
                                setFormData(prev => ({
                                    ...prev,
                                    locations: newValue
                                }));
                                onHandleLocationRegionChange(newValue, 'locations');
                            }}
                        >
                        </MultiSelect>
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <MultiSelect 
                            label='Location State *'
                            isFilterObject
                            variant={"standard"}
                            options={data.filterStates}
                            value={formData.states}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.states, "states", formData);
                                setFormData(prev => ({
                                    ...prev,
                                    states: newValue
                                }));
                            }}
                        >
                        </MultiSelect>
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <MultiSelect 
                            label='Refrigerant Type'
                            isFilterObject
                            variant={"standard"}
                            options={data.refrigerantType}
                            value={formData.refrigerantType}
                            onChange={e => {
                                const newValue = getValueOrDefault(e, data.refrigerantType, "refrigerantType", formData);
                                const gwp = getGWP(newValue, data.refrigerantGWP);
                                setMaxGWP(gwp);
                                setFormData(prev => ({
                                    ...prev,
                                    refrigerantType: newValue,
                                    refrigerantGwpMax: gwp
                                }));
                            }}
                        >
                        </MultiSelect>
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <RangeInput
                            label='REFRIGERANT TYPE GWP VALUE *'
                            startRange={formData.refrigerantGwpMin}
                            endRange={formData.refrigerantGwpMax}
                            onClose={(start, end) => setFormData(prev => ({
                                ...prev,
                                refrigerantGwpMin: start,
                                refrigerantGwpMax: end,
                            }))}
                        />
                    </Grid>
                    <Grid item xs={8} lg={8} sx={{ marginLeft: 12 }} align='center'>
                        <RangeInput
                            label={`ASSET CHARGE CAPACITY(${UserInfo.weightUnits}) *`}
                            startRange={formData.chargeCapacityMin}
                            endRange={formData.chargeCapacityMax}
                            onClose={(start, end) => setFormData(prev => ({
                                ...prev,
                                chargeCapacityMin: start,
                                chargeCapacityMax: end,
                            }))}
                        />
                    </Grid>

                    <Grid item xs={12} lg={12} align='center'>
                        <Box mb={2} sx={{ width: '65%' }}>
                            <PrimaryButton
                                fullWidth
                                size='large'
                                variant='contained'
                                onClick={exportReport}
                                buttonText={"GENERATE REPORT (.zip)"}
                            >
                            </PrimaryButton>
                        </Box>
                    </Grid>
                </Grid>
            </CommonModal>
        </>
    );
};

export default ChronicallyLeaking608;
